// Lightweight keyboard-shortcut overlay for the Decision Engine page.
//
// Behaviour (per Phase 3 Step 2 brief):
//   • `?`                       → toggle the overlay open/closed.
//                                 IGNORED when an editable element is focused
//                                 (input / textarea / contentEditable). This
//                                 stops accidental triggers while typing.
//   • Esc                       → close the overlay if open.
//   • Cmd+K  /  Ctrl+K          → focus the input. Works EVERYWHERE
//                                 (even while input is focused — by spec).
//   • Cmd+Shift+C  /  Ctrl+...  → clear the current session.
//
// The component owns ZERO global state. Local `useState` for open flag plus
// a single window-level `keydown` listener. All side-effects (focus, clear)
// are delegated to props/refs supplied by the page.
import React, { useEffect, useState } from 'react';
import { Keyboard, X } from 'lucide-react';

import { useDecisionEngine } from './state/DecisionEngineContext';

// Detect whether the current focus is on an editable element. Used to
// suppress the bare `?` toggle so users can keep typing normally.
const _isEditableTarget = (target) => {
  if (!target) return false;
  const tag = (target.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (target.isContentEditable) return true;
  return false;
};

const SHORTCUTS = [
  { keys: ['↑', '↓'], action: 'Navigate dropdown' },
  { keys: ['Tab', 'Enter'], action: 'Select highlighted item' },
  { keys: ['/'], action: 'Open command palette' },
  { keys: ['@'], action: 'Open player picker' },
  { keys: ['⌘', 'K'], action: 'Focus input (Ctrl+K on Windows/Linux)' },
  { keys: ['⌘', '⇧', 'C'], action: 'Clear current session (Ctrl+Shift+C)' },
  { keys: ['?'], action: 'Show / hide this overlay' },
  { keys: ['Esc'], action: 'Close overlay or dismiss dropdown' },
];

export const KeyboardShortcuts = ({ onClearSession }) => {
  const [open, setOpen] = useState(false);
  const { refs } = useDecisionEngine();
  const { inputRef } = refs;

  useEffect(() => {
    const onKey = (e) => {
      const editable = _isEditableTarget(e.target);
      const meta = e.metaKey || e.ctrlKey;

      // Cmd+K / Ctrl+K — focus the input. Works everywhere.
      if (meta && !e.shiftKey && !e.altKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        inputRef?.current?.focus();
        return;
      }

      // Cmd+Shift+C / Ctrl+Shift+C — clear the current session. Works
      // everywhere — modifier+shift means it can't be hit accidentally.
      if (meta && e.shiftKey && !e.altKey && (e.key === 'c' || e.key === 'C')) {
        e.preventDefault();
        if (typeof onClearSession === 'function') onClearSession();
        return;
      }

      // Esc closes the overlay if it's open. We don't preventDefault here so
      // dropdowns / dialogs that already listen for Esc still get it when
      // the overlay isn't visible.
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
        return;
      }

      // Bare `?` toggles the overlay — but only when the user isn't typing.
      if (e.key === '?' && !meta && !e.altKey && !editable) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClearSession, inputRef]);

  if (!open) {
    // Floating discoverability hint (bottom-left, low-contrast). Clickable
    // for users who don't know about `?`.
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 left-4 z-30 inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur px-2.5 py-1.5 text-[11px] text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:border-indigo-300 dark:hover:border-indigo-700 shadow-sm"
        aria-label="Show keyboard shortcuts"
        data-testid="keyboard-shortcuts-trigger"
      >
        <Keyboard className="h-3 w-3" />
        <kbd className="font-mono text-[10px] px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800">?</kbd>
        <span>Shortcuts</span>
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      data-testid="keyboard-shortcuts-overlay"
      onClick={(e) => {
        // Click outside the dialog body closes the overlay.
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div
        className="w-[420px] max-w-[92vw] rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl"
        data-testid="keyboard-shortcuts-panel"
      >
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 px-4 py-3">
          <div className="flex items-center gap-2">
            <Keyboard className="h-4 w-4 text-indigo-600" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Keyboard shortcuts
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            aria-label="Close shortcuts"
            data-testid="keyboard-shortcuts-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {SHORTCUTS.map(({ keys, action }) => (
            <li
              key={action}
              className="flex items-center justify-between px-4 py-2.5 text-sm"
              data-testid={`keyboard-shortcut-row-${keys.join('-')}`}
            >
              <span className="text-gray-600 dark:text-gray-300">{action}</span>
              <span className="flex items-center gap-1">
                {keys.map((k, i) => (
                  <React.Fragment key={i}>
                    <kbd className="font-mono text-[11px] px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                      {k}
                    </kbd>
                    {i < keys.length - 1 && (
                      <span className="text-gray-300 dark:text-gray-600 text-[10px]">+</span>
                    )}
                  </React.Fragment>
                ))}
              </span>
            </li>
          ))}
        </ul>
        <div className="px-4 py-2.5 text-[11px] text-gray-400 border-t border-gray-100 dark:border-gray-800">
          Tip: press <kbd className="font-mono px-1 rounded bg-gray-100 dark:bg-gray-800">?</kbd> any time
          to toggle this panel.
        </div>
      </div>
    </div>
  );
};
